import { sortedLevels } from "building-generation/elements/level/levelTools";
import type { Asset } from "building-types/entities/asset";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorMaterial, InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type {
  ExteriorMaterialOverride,
  FBXUserPropertiesMaterialChoice,
  InteriorMaterialOverride,
  MaterialSrcOverride,
} from "building-types/materials/materialTypes";
import type { Palette } from "building-types/materials/palette";
import { getBuiltinProp } from "building-types/props/getBuiltinPropLibrary";
import { PropCategory } from "building-types/props/propCategoryTypes";
import type { Prop, PropAttributes, PropAttributeTable, PropWithAssetId } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ViewFilterProps } from "building-types/views/viewFilterTypes";
import type { Table } from "common/data/table";
import { materialCacheKey, type MaterialSrc } from "common/materials/materialSrc";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { mergedPropLibraryNew } from "doc/propTools";
import type { BRep, Extrusion, Face } from "geom/brep";
import type { Geom } from "geom/geom";
import type GeomTagGroup from "geom/geomTagGroup";
import { tessBRep, tessBreps, tessExtrusion, tessExtrusions, tessFaces3 } from "geom/tess";
import type { UVTransformation } from "geom/uv";
import { UVGeneration } from "geom/uv";
import { extrusionsToTHREE, facesToTHREE, gltfToTHREE, mesh3ToTHREE } from "gfx/convert";
import { assignToBuildingExteriorLayer, assignToInteriorLayer } from "gfx/layers";
import type { MaterialFactory } from "gfx/MaterialFactory";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import { propSetMaterial } from "lib/house/materialMapping";
import type * as THREE from "three";
import type { Object3D } from "three";
import { Euler, Group, Vector3 } from "three";
import { materialFromPalette } from "./materialFromPalette";
import type RenderContext from "./RenderContext";
import { assignMeshReportingUnitOverrideMaterial } from "./utils/reportingUnitUtil";

export type FacesWithUVTransformation = {
  faces: Face[];
  materialTransformation?: UVTransformation;
};

export type MaterialAndUVToFaces = {
  [materialId: string]: {
    [uvGen: number]: FacesWithUVTransformation[];
  };
};

export type ExtrusionWithUVTransformation = {
  extrusion: Extrusion;
  materialTransformation?: UVTransformation;
};

export type MaterialAndUVToExtrusions = {
  [materialId: string]: {
    [uvGen: number]: ExtrusionWithUVTransformation[];
  };
};

export type MaterialAndUVToIntegerIds = {
  [materialId: string]: {
    [uvGen: number]: number[];
  };
};

export function placeGeoByMaterial(
  materialId: string,
  uvGen: UVGeneration,
  geo: Geom,
  integerId: number | number[] | undefined = undefined,
  materialToFaces: MaterialAndUVToFaces,
  materialToExtrusions: MaterialAndUVToExtrusions,
  materialToFaceIntegerIds?: MaterialAndUVToIntegerIds,
  materialToExtrusionIntegerIds?: MaterialAndUVToIntegerIds,
  materialTransformation?: UVTransformation,
) {
  switch (geo.type) {
    case "BREP": {
      if (!materialToFaces[materialId]) {
        materialToFaces[materialId] = {};
      }
      const list = materialToFaces[materialId][uvGen] || [];
      list.push({ faces: geo.faces, materialTransformation });
      materialToFaces[materialId][uvGen] = list;
      if (materialToFaceIntegerIds !== undefined) {
        if (!materialToFaceIntegerIds[materialId]) {
          materialToFaceIntegerIds[materialId] = {};
        }
        const idList = materialToFaceIntegerIds[materialId][uvGen] || [];
        idList.push(...new Array(geo.faces.length).fill(integerId, 0, geo.faces.length));
        materialToFaceIntegerIds[materialId][uvGen] = idList;
      }
      break;
    }
    case "FACE": {
      if (!materialToFaces[materialId]) {
        materialToFaces[materialId] = {};
      }
      const list = materialToFaces[materialId][uvGen] || [];
      list.push({ faces: [geo], materialTransformation });
      materialToFaces[materialId][uvGen] = list;
      if (materialToFaceIntegerIds !== undefined) {
        if (!materialToFaceIntegerIds[materialId]) {
          materialToFaceIntegerIds[materialId] = {};
        }
        const idList = materialToFaceIntegerIds[materialId][uvGen] || [];
        if (Array.isArray(integerId)) {
          idList.push(...integerId);
        } else if (integerId !== undefined) {
          idList.push(integerId);
        }

        materialToFaceIntegerIds[materialId][uvGen] = idList;
      }
      break;
    }
    case "EXTRUSION": {
      if (!materialToExtrusions[materialId]) {
        materialToExtrusions[materialId] = {};
      }
      const list = materialToExtrusions[materialId][uvGen] || [];
      list.push({ extrusion: geo, materialTransformation });
      materialToExtrusions[materialId][uvGen] = list;
      if (materialToExtrusionIntegerIds !== undefined) {
        if (!materialToExtrusionIntegerIds[materialId]) {
          materialToExtrusionIntegerIds[materialId] = {};
        }
        const idList = materialToExtrusionIntegerIds[materialId][uvGen] || [];
        if (Array.isArray(integerId)) {
          idList.push(...integerId);
        } else if (integerId !== undefined) {
          idList.push(integerId);
        }

        materialToExtrusionIntegerIds[materialId][uvGen] = idList;
      }
      break;
    }
    case "FACE_GROUP": {
      if (!materialToFaces[materialId]) {
        materialToFaces[materialId] = {};
      }
      const list = materialToFaces[materialId][uvGen] || [];
      list.push({ faces: geo.faces, materialTransformation });
      materialToFaces[materialId][uvGen] = list;
      if (materialToFaceIntegerIds !== undefined) {
        if (!materialToFaceIntegerIds[materialId]) {
          materialToFaceIntegerIds[materialId] = {};
        }
        const idList = materialToFaceIntegerIds[materialId][uvGen] || [];
        idList.push(...new Array(geo.faces.length).fill(integerId, 0, geo.faces.length));
        materialToFaceIntegerIds[materialId][uvGen] = idList;
      }
      break;
    }
  }
}

type GroupsByLevel = { [levelId: string]: Group };

export function groupWithSortedLevelSubgroups(levels: Table<Level>): [Group, GroupsByLevel] {
  const parentGroup = new Group();
  const groupsByLevel: GroupsByLevel = {};
  parentGroup.children = sortedLevels(levels).map((level) => {
    const group = new Group();
    group.userData.levelId = level.id;
    groupsByLevel[level.id] = group;
    return group;
  });
  return [parentGroup, groupsByLevel];
}

/**
 * @param group
 * @param groups
 * @param palette
 * @param ctx
 * @param setToExteriorLayer
 * @param clip
 * @param integerId Either a single integer id or an array of ids. If it's an array, then there should be one id per group.
 * @returns
 */
export async function renderExteriorGroups(
  group: THREE.Group,
  groups: GeomTagGroup<ExteriorMaterialOverride>[],
  palette: ExteriorPalette | undefined,
  ctx: RenderContext,
  applySelectedReportingUnitMaterials: boolean, // Set to false for things like site.
  setToExteriorLayer: boolean = true,
  clip?: boolean,
  integerId?: number | number[],
  reportingUnitLabelId?: string,
) {
  if (!groups) {
    return;
  }

  const singleIntegerId = integerId === undefined || typeof integerId === "number";
  if (!(singleIntegerId || (integerId as number[]).length === groups.length)) {
    console.assert(false, "integerId array length should match groups length");
  }

  // resolve material ids
  const resolvedMaterials = {};

  const mf = ctx.materialFactory;

  for (const { tags } of groups) {
    for (const { exteriorMaterial } of tags) {
      assertIsNotUndefined(exteriorMaterial);
      if (resolvedMaterials[exteriorMaterial]) {
        continue;
      }

      assertIsNotUndefined(palette);
      resolvedMaterials[exteriorMaterial] = await materialFromPalette(mf, palette, exteriorMaterial);
    }
  }

  // merge by material/geometry type
  const materialToFaces: MaterialAndUVToFaces = {};
  const materialToExtrusions: MaterialAndUVToExtrusions = {};
  const materialToFaceIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};
  const materialToExtrusionIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};

  for (let groupIndex = 0, groupsLen = groups.length; groupIndex < groupsLen; ++groupIndex) {
    const { geom, tags } = groups[groupIndex];
    for (let i = 0, l = geom.length; i < l; i++) {
      const { exteriorMaterial, uvGen, materialTransformation } = tags[i];
      const geo = geom[i];

      assertIsNotUndefined(exteriorMaterial);

      placeGeoByMaterial(
        exteriorMaterial,
        uvGen,
        geo,
        singleIntegerId ? integerId : integerId[groupIndex],
        materialToFaces,
        materialToExtrusions,
        materialToFaceIntegerIds,
        materialToExtrusionIntegerIds,
        materialTransformation,
      );
    }
  }

  // tessellate
  for (const id in materialToFaces) {
    const currMaterial = materialToFaces[id];
    const currIntegerIds = singleIntegerId || !materialToFaceIntegerIds ? undefined : materialToFaceIntegerIds[id];
    for (const uvGen in currMaterial) {
      let cumulativeFacesCount = 0;
      for (const facesWithUVXForm of currMaterial[uvGen]) {
        const facesLen = facesWithUVXForm.faces.length;
        const mesh = convertFacesWithUVGeneration(
          facesWithUVXForm.faces,
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds
            ? integerId
            : currIntegerIds[uvGen].slice(cumulativeFacesCount, cumulativeFacesCount + facesLen),
          facesWithUVXForm.materialTransformation,
        );
        if (setToExteriorLayer) {
          assignToBuildingExteriorLayer(mesh, clip);
        }
        if (applySelectedReportingUnitMaterials) {
          await assignMeshReportingUnitOverrideMaterial(mesh, ctx, reportingUnitLabelId);
        }
        group.add(mesh);
        cumulativeFacesCount += facesLen;
      }
    }
  }

  for (const id in materialToExtrusions) {
    const currMaterial = materialToExtrusions[id];
    const currIntegerIds =
      singleIntegerId || !materialToExtrusionIntegerIds ? undefined : materialToExtrusionIntegerIds[id];
    for (const uvGen in currMaterial) {
      let extrusionIndex = 0;
      for (const extrusionWithUVXform of currMaterial[uvGen]) {
        const mesh = convertExtrusionsWithUVGeneration(
          [extrusionWithUVXform.extrusion],
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds ? integerId : currIntegerIds[uvGen][extrusionIndex],
          extrusionWithUVXform.materialTransformation,
        );
        if (setToExteriorLayer) {
          assignToBuildingExteriorLayer(mesh, clip);
        }
        if (applySelectedReportingUnitMaterials) {
          await assignMeshReportingUnitOverrideMaterial(mesh, ctx, reportingUnitLabelId);
        }
        group.add(mesh);
        extrusionIndex++;
      }
    }
  }
}

export async function renderInteriorGroups(
  group: THREE.Group,
  groups: GeomTagGroup<InteriorMaterialOverride>[],
  palette: InteriorPalette,
  room: Room | undefined,
  ctx: RenderContext,
  clip?: boolean,
  materialOverrideTable?: { [key: string]: MaterialSrc },
  integerId?: number | number[],
) {
  if (!room || !groups) {
    return;
  }

  const singleIntegerId = integerId === undefined || typeof integerId === "number";
  if (!(singleIntegerId || (integerId as number[]).length === groups.length)) {
    console.assert(false);
  }

  // resolve material ids
  const resolvedMaterials = {};

  const mf = ctx.materialFactory;

  for (const { tags } of groups) {
    for (const { interiorMaterial } of tags) {
      if (resolvedMaterials[interiorMaterial]) {
        continue;
      }

      if (materialOverrideTable && materialOverrideTable[interiorMaterial]) {
        resolvedMaterials[interiorMaterial] = await ctx.materialFactory.getDesignMaterial(
          materialOverrideTable[interiorMaterial],
        );
      } else {
        resolvedMaterials[interiorMaterial] = await materialFromPalette(mf, palette, interiorMaterial, room.type);
      }
    }
  }

  // merge by material/geometry type
  const materialToFaces: MaterialAndUVToFaces = {};
  const materialToExtrusions: MaterialAndUVToExtrusions = {};
  const materialToFaceIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};
  const materialToExtrusionIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};

  for (let groupIndex = 0, groupsLen = groups.length; groupIndex < groupsLen; ++groupIndex) {
    const { geom, tags } = groups[groupIndex];
    for (let i = 0, l = geom.length; i < l; i++) {
      const { interiorMaterial, uvGen } = tags[i];
      const geo = geom[i];

      placeGeoByMaterial(
        interiorMaterial,
        uvGen,
        geo,
        singleIntegerId ? integerId : integerId[groupIndex],
        materialToFaces,
        materialToExtrusions,
        materialToFaceIntegerIds,
        materialToExtrusionIntegerIds,
      );
    }
  }

  // tessellate
  for (const id in materialToFaces) {
    const currMaterial = materialToFaces[id];
    const currIntegerIds = singleIntegerId || !materialToFaceIntegerIds ? undefined : materialToFaceIntegerIds[id];
    for (const uvGen in currMaterial) {
      let cumulativeFacesCount = 0;
      for (const facesWithUVXForm of currMaterial[uvGen]) {
        const facesLen = facesWithUVXForm.faces.length;
        const mesh = convertFacesWithUVGeneration(
          facesWithUVXForm.faces,
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds
            ? integerId
            : currIntegerIds[uvGen].slice(cumulativeFacesCount, cumulativeFacesCount + facesLen),
          facesWithUVXForm.materialTransformation,
        );
        assignToInteriorLayer(mesh, clip);
        group.add(mesh);
        cumulativeFacesCount += facesLen;
      }
    }
  }

  for (const id in materialToExtrusions) {
    const currMaterial = materialToExtrusions[id];
    const currIntegerIds =
      singleIntegerId || !materialToExtrusionIntegerIds ? undefined : materialToExtrusionIntegerIds[id];
    for (const uvGen in currMaterial) {
      let extrusionIndex = 0;
      for (const extrusionWithUVXform of currMaterial[uvGen]) {
        const mesh = convertExtrusionsWithUVGeneration(
          [extrusionWithUVXform.extrusion],
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds ? integerId : currIntegerIds[uvGen][extrusionIndex],
          extrusionWithUVXform.materialTransformation,
        );
        assignToInteriorLayer(mesh, clip);
        group.add(mesh);
        extrusionIndex++;
      }
    }
  }
}

export async function renderInteriorNoRoomGroups(
  group: THREE.Group,
  groups: GeomTagGroup<InteriorMaterial>[],
  palette: InteriorPalette,
  ctx: RenderContext,
  clip?: boolean,
  materialOverrideTable?: { [key: string]: MaterialSrc },
  integerId?: number,
) {
  if (!groups) {
    return;
  }

  // resolve material ids
  const resolvedMaterials: { [matId: string]: THREE.Material | undefined } = {};

  const mf = ctx.materialFactory;

  for (const { tags } of groups) {
    for (const matid of tags) {
      if (resolvedMaterials[matid]) {
        continue;
      }

      if (materialOverrideTable && materialOverrideTable[matid]) {
        resolvedMaterials[matid] = await ctx.materialFactory.getDesignMaterial(materialOverrideTable[matid]);
      } else {
        resolvedMaterials[matid] = await materialFromPalette(mf, palette, matid);
      }
    }
  }

  // merge by material/geometry type
  const materialToFaces: { [materialId: string]: Face[] } = {};
  const materialToExtrusions: { [materialId: string]: Extrusion[] } = {};

  for (const { geom, tags } of groups) {
    for (let i = 0, l = geom.length; i < l; i++) {
      const mat = tags[i];
      const geo = geom[i];

      switch (geo.type) {
        case "BREP": {
          const list = materialToFaces[mat] || [];
          list.push(...geo.faces);
          materialToFaces[mat] = list;
          break;
        }
        case "FACE": {
          const list = materialToFaces[mat] || [];
          list.push(geo);
          materialToFaces[mat] = list;
          break;
        }
        case "EXTRUSION": {
          const list = materialToExtrusions[mat] || [];
          list.push(geo);
          materialToExtrusions[mat] = list;
          break;
        }
        case "FACE_GROUP": {
          const list = materialToFaces[mat] || [];
          list.push(...geo.faces);
          materialToFaces[mat] = list;
          break;
        }
      }
    }
  }

  // tessellate
  for (const id in materialToFaces) {
    const mesh = facesToTHREE(materialToFaces[id], resolvedMaterials[id], integerId);
    assignToInteriorLayer(mesh, clip);
    group.add(mesh);
  }

  for (const id in materialToExtrusions) {
    const mesh = extrusionsToTHREE(materialToExtrusions[id], resolvedMaterials[id], integerId);
    assignToInteriorLayer(mesh, clip);
    group.add(mesh);
  }
}

export async function renderDirectGroups(
  group: THREE.Group,
  groups: GeomTagGroup<MaterialSrcOverride>[],
  ctx: RenderContext,
  defaultMaterialSrc: MaterialSrc,
  setToExteriorLayer: boolean,
  clip: boolean,
  integerId?: number | number[],
) {
  if (!groups) {
    return;
  }

  const singleIntegerId = integerId === undefined || typeof integerId === "number";
  if (!(singleIntegerId || (integerId as number[]).length === groups.length)) {
    console.assert(false);
  }

  // resolve material ids
  const resolvedMaterials = {};

  const mf = ctx.materialFactory;

  for (const { tags } of groups) {
    for (const { materialSrc = defaultMaterialSrc } of tags) {
      const materialKey = materialCacheKey(materialSrc);
      if (resolvedMaterials[materialKey]) {
        continue;
      }

      resolvedMaterials[materialKey] = await mf.getDesignMaterial(materialSrc);
    }
  }

  // merge by material/geometry type
  const materialToFaces: MaterialAndUVToFaces = {};
  const materialToExtrusions: MaterialAndUVToExtrusions = {};
  const materialToFaceIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};
  const materialToExtrusionIntegerIds: MaterialAndUVToIntegerIds | undefined = singleIntegerId ? undefined : {};

  for (let groupIndex = 0, groupsLen = groups.length; groupIndex < groupsLen; ++groupIndex) {
    const { geom, tags } = groups[groupIndex];
    for (let i = 0, l = geom.length; i < l; i++) {
      const { materialSrc = defaultMaterialSrc, uvGen } = tags[i];
      const materialKey = materialCacheKey(materialSrc);
      const geo = geom[i];

      placeGeoByMaterial(
        materialKey,
        uvGen,
        geo,
        singleIntegerId ? integerId : integerId[groupIndex],
        materialToFaces,
        materialToExtrusions,
        materialToFaceIntegerIds,
        materialToExtrusionIntegerIds,
      );
    }
  }

  // tessellate
  for (const id in materialToFaces) {
    const currMaterial = materialToFaces[id];
    const currIntegerIds = singleIntegerId || !materialToFaceIntegerIds ? undefined : materialToFaceIntegerIds[id];
    for (const uvGen in currMaterial) {
      let cumulativeFacesCount = 0;
      for (const facesWithUVXForm of currMaterial[uvGen]) {
        const facesLen = facesWithUVXForm.faces.length;
        const mesh = convertFacesWithUVGeneration(
          facesWithUVXForm.faces,
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds
            ? integerId
            : currIntegerIds[uvGen].slice(cumulativeFacesCount, cumulativeFacesCount + facesWithUVXForm.faces.length),
          facesWithUVXForm.materialTransformation,
        );
        if (setToExteriorLayer) {
          assignToBuildingExteriorLayer(mesh, clip);
        } else {
          assignToInteriorLayer(mesh, clip);
        }
        group.add(mesh);
        cumulativeFacesCount += facesLen;
      }
    }
  }

  for (const id in materialToExtrusions) {
    const currMaterial = materialToExtrusions[id];
    const currIntegerIds =
      singleIntegerId || !materialToExtrusionIntegerIds ? undefined : materialToExtrusionIntegerIds[id];
    for (const uvGen in currMaterial) {
      let extrusionIndex = 0;
      for (const extrusionWithUVXform of currMaterial[uvGen]) {
        const mesh = convertExtrusionsWithUVGeneration(
          [extrusionWithUVXform.extrusion],
          resolvedMaterials[id],
          parseInt(uvGen),
          singleIntegerId || !currIntegerIds ? integerId : currIntegerIds[uvGen][extrusionIndex],
          extrusionWithUVXform.materialTransformation,
        );
        if (setToExteriorLayer) {
          assignToBuildingExteriorLayer(mesh, clip);
        } else {
          assignToInteriorLayer(mesh, clip);
        }
        group.add(mesh);
        extrusionIndex++;
      }
    }
  }
}

export function convertExtrusionWithUVGeneration(
  ext: Extrusion,
  material: THREE.Material,
  uvGen: UVGeneration = UVGeneration.GLOBAL_RECT_UV,
  id?: number,
  xform?: UVTransformation,
) {
  const mesh = tessExtrusion(ext, id, uvGen, undefined, xform);
  return mesh3ToTHREE(mesh, material);
}

export function convertExtrusionsWithUVGeneration(
  exts: Extrusion[],
  material: THREE.Material | undefined,
  uvGen: UVGeneration = UVGeneration.GLOBAL_RECT_UV,
  id?: number | number[],
  xform?: UVTransformation | UVTransformation[],
) {
  const mesh = tessExtrusions(exts, id, uvGen, undefined, xform);
  return mesh3ToTHREE(mesh, material);
}

export function convertBRepsWithUVGeneration(
  breps: BRep[],
  material: THREE.Material | undefined,
  uvGen: UVGeneration = UVGeneration.GLOBAL_RECT_UV,
  id?: number | number[],
  xform?: UVTransformation | UVTransformation[],
) {
  const mesh = tessBreps(breps, id, uvGen, xform);
  return mesh3ToTHREE(mesh, material);
}

export function convertFacesWithUVGeneration(
  faces: Face[],
  material: THREE.Material | undefined,
  uvGen: UVGeneration = UVGeneration.GLOBAL_RECT_UV,
  id?: number | number[] | (number | undefined)[],
  xform?: UVTransformation | UVTransformation[] | (UVTransformation | undefined)[],
) {
  const mesh = tessFaces3(faces, id, uvGen, undefined, xform);
  return mesh3ToTHREE(mesh, material);
}

export function convertBRepWithUVGeneration(
  brep: BRep,
  material: THREE.Material | undefined,
  uvGen: UVGeneration = UVGeneration.GLOBAL_RECT_UV,
  id?: number,
  xform?: UVTransformation,
) {
  const mesh = tessBRep(brep, id, uvGen, xform);
  return mesh3ToTHREE(mesh, material);
}

export async function loadPropNoMaterials(
  prop: Omit<PropWithAssetId, "levelId" | "aboveLevel" | "faceCategory">,
  assets?: Table<Asset>,
): Promise<[null, undefined] | [THREE.Object3D, string]> {
  if (prop.assetId === undefined) {
    return [null, undefined];
  }
  const propAsset = assets ? await mergedPropLibraryNew(assets).get(prop.assetId) : await getBuiltinProp(prop.assetId);
  if (!propAsset) {
    return [null, undefined];
  }

  const gltfObject: Object3D | undefined = await THREEAssetLoader.instance.loadPropAssetGltf(propAsset);
  if (!gltfObject) {
    return [null, undefined];
  }
  const object = await gltfToTHREE(gltfObject);

  object.position.x = prop.x;
  object.position.y = prop.y;
  object.position.z = prop.z;

  object.scale.x *= prop.scaleX;
  object.scale.y *= prop.scaleY;
  object.scale.z *= prop.scaleZ;

  object.setRotationFromEuler(new Euler());
  const rotAxis = new Vector3(0, 0, 1);
  object.rotateOnAxis(rotAxis, degreeToRadian(prop.rotationDegrees));

  object.updateMatrix();
  object.updateMatrixWorld();
  object.userData.isProp = true;

  return [object, propAsset.name];
}

export function resolvePropAssetId(prop: Prop, propCatalog: PropAttributeTable) {
  const propAttributes = propCatalog[prop.src];
  const assetId = propAttributes?.propAssetId;
  return assetId;
}

export async function loadPropFromSettings(
  prop: Prop,
  propCatalog: PropAttributeTable,
  exteriorPalette: Palette | null,
  interiorPalette: Palette | null,
  materialFactory: MaterialFactory | undefined,
  materialMapping?: (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice,
  assets?: Table<Asset>,
): Promise<THREE.Object3D | null> {
  const assetId = resolvePropAssetId(prop, propCatalog);
  const [object] = await loadPropNoMaterials({ ...prop, assetId }, assets);

  if (!object) {
    return null;
  }

  if (materialFactory) {
    await propSetMaterial(
      prop,
      materialFactory,
      object,
      exteriorPalette,
      interiorPalette,
      materialMapping,
      propCatalog,
    );
  }

  return object;
}

export const PropCategoryToViewFilterField: Record<PropCategory, keyof ViewFilterProps> = {
  [PropCategory.APPLIANCES]: "propAppliances",
  [PropCategory.FIXTURES]: "propFixtures",
  [PropCategory.FURNITURE]: "propFurniture",
  [PropCategory.OTHER]: "propOther",
  [PropCategory.FOLIAGE]: "propFoliage",
};

export function propCategoryFilter(viewFilter: ViewFilterProps, propAttributes: PropAttributes): boolean {
  const viewFilterField = PropCategoryToViewFilterField[propAttributes.category];
  return viewFilter[viewFilterField];
}
